/**
 * @audit GAP-FE-B005 · raw HTTP retained — see `shared/services/AUDIT_HTTP_DEBT.md`
 *
 * Severity: MEDIUM
 * Reason:  /auth/telemetry — write-only / fire-and-forget; acceptable inline
 *
 * When this service no longer holds `inject(HttpClient)` or hand-rolled
 * `this.http.X` calls (either by migrating to `@api` or sequestering
 * to a `*-bridge.service.ts`), remove this banner and the matching row
 * in `AUDIT_HTTP_DEBT.md`.
 */
import { Injectable, inject, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subject, interval, Observable, of } from 'rxjs';
import { catchError, retry, take } from 'rxjs/operators';

import { LoggerService } from './logger.service';
import {
  AuthError,
  AuthErrorCode,
  AuthErrorSeverity,
  AuthErrorCategory
} from '../models/auth-errors.model';

/**
 * Telemetry event for auth errors
 */
export interface AuthTelemetryEvent {
  /** Event type */
  type: 'auth_error' | 'auth_success' | 'auth_retry' | 'auth_recovery';

  /** Timestamp of the event */
  timestamp: Date;

  /** Error code (for error events) */
  errorCode?: AuthErrorCode;

  /** Error category */
  category?: AuthErrorCategory;

  /** Error severity */
  severity?: AuthErrorSeverity;

  /** HTTP status code (if applicable) */
  statusCode?: number;

  /** Was the error retryable */
  retryable?: boolean;

  /** Number of retries attempted */
  retryCount?: number;

  /** Time taken for the request (ms) */
  duration?: number;

  /** Action that triggered the error */
  action?: string;

  /** User ID (anonymized) */
  userId?: string;

  /** Session ID (anonymized) */
  sessionId?: string;

  /** User agent (for debugging) */
  userAgent?: string;

  /** Additional context data */
  context?: Record<string, unknown>;
}

/**
 * Aggregated metrics for a time window
 */
export interface AuthTelemetryMetrics {
  /** Time window start */
  windowStart: Date;

  /** Time window end */
  windowEnd: Date;

  /** Total errors in window */
  totalErrors: number;

  /** Total successes in window */
  totalSuccesses: number;

  /** Error rate (errors / total) */
  errorRate: number;

  /** Errors by code */
  errorsByCode: Record<string, number>;

  /** Errors by category */
  errorsByCategory: Record<string, number>;

  /** Errors by severity */
  errorsBySeverity: Record<string, number>;

  /** Average retry count */
  avgRetryCount: number;

  /** Average request duration */
  avgDuration: number;

  /** P95 request duration */
  p95Duration: number;
}

/**
 * Telemetry configuration
 */
export interface AuthTelemetryConfig {
  /** Enable telemetry collection */
  enabled: boolean;

  /** Enable console logging of telemetry events */
  logToConsole: boolean;

  /** Enable sending events to backend */
  sendToBackend: boolean;

  /** Backend endpoint URL */
  backendUrl?: string;

  /** Batch size for sending events */
  batchSize: number;

  /** Flush interval in milliseconds */
  flushInterval: number;

  /** Maximum events to buffer */
  maxBufferSize: number;

  /** Sample rate (0-1) for high-volume events */
  sampleRate: number;

  /** Anonymize user/session IDs */
  anonymizeIds: boolean;

  /** Include User-Agent in telemetry events (opt-in for PII considerations) */
  includeUserAgent: boolean;
}

/**
 * Default telemetry configuration
 */
export const DEFAULT_AUTH_TELEMETRY_CONFIG: AuthTelemetryConfig = {
  enabled: true,
  logToConsole: false,
  sendToBackend: false,
  backendUrl: '/api/v1/telemetry/auth',
  batchSize: 10,
  flushInterval: 30000, // 30 seconds
  maxBufferSize: 1000,
  sampleRate: 1.0,
  anonymizeIds: true,
  includeUserAgent: false
};

/**
 * Telemetry event handler type
 */
export type TelemetryEventHandler = (event: AuthTelemetryEvent) => void;

/**
 * AuthTelemetryService
 * DEV-267: Low Priority - Telemetry integration for error monitoring
 *
 * Provides comprehensive telemetry collection for authentication errors:
 * - Real-time event tracking
 * - Aggregated metrics
 * - Multiple output backends (console, HTTP, custom handlers)
 * - Sampling and batching for performance
 * - Privacy-first with ID anonymization
 *
 * Usage:
 * ```typescript
 * // Inject the service
 * private telemetry = inject(AuthTelemetryService);
 *
 * // Track an auth error
 * this.telemetry.trackAuthError(error, { action: 'login' });
 *
 * // Track success
 * this.telemetry.trackAuthSuccess('login', userId);
 *
 * // Get current metrics
 * const metrics = this.telemetry.getMetrics();
 *
 * // Subscribe to events
 * this.telemetry.events$.subscribe(event => this.logger.debug('Telemetry event', { event }));
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class AuthTelemetryService {
  private readonly logger = inject(LoggerService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Telemetry configuration
   */
  private config: AuthTelemetryConfig = { ...DEFAULT_AUTH_TELEMETRY_CONFIG };

  /**
   * Event buffer for batching
   */
  private eventBuffer: AuthTelemetryEvent[] = [];

  /**
   * Custom event handlers
   */
  private eventHandlers: TelemetryEventHandler[] = [];

  /**
   * Subject for streaming events
   */
  private readonly eventsSubject = new Subject<AuthTelemetryEvent>();

  /**
   * Observable stream of telemetry events
   */
  readonly events$ = this.eventsSubject.asObservable();

  /**
   * Metrics aggregation window (last 5 minutes)
   */
  private metricsWindow: AuthTelemetryEvent[] = [];
  private readonly METRICS_WINDOW_MS = 5 * 60 * 1000;

  /**
   * Maximum metrics window size to prevent unbounded growth
   */
  private readonly MAX_METRICS_WINDOW_SIZE = 10000;

  /**
   * Current session ID (generated on service init)
   */
  private sessionId = this.generateSessionId();

  /**
   * Pseudonymous ID cache for session-scoped anonymization
   */
  private readonly pseudonymousCache = new Map<string, string>();
  private pseudonymousCounter = 0;

  /**
   * Error counts signal for reactive UI
   */
  private readonly _errorCounts = signal<Record<AuthErrorCode, number>>(
    {} as Record<AuthErrorCode, number>
  );

  /**
   * Computed total error count
   */
  readonly totalErrors = computed(() => {
    const counts = this._errorCounts();
    return Object.values(counts).reduce((sum, count) => sum + count, 0);
  });

  /**
   * Computed error counts by code
   */
  readonly errorCounts = computed(() => this._errorCounts());

  constructor() {
    // Clean up old events from metrics window periodically
    interval(60000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cleanupMetricsWindow());
  }

  /**
   * Configure telemetry service
   */
  configure(config: Partial<AuthTelemetryConfig>): void {
    this.config = { ...this.config, ...config };
    this.logger.debug('[AuthTelemetry] Configuration updated', { config: this.config });
  }

  /**
   * Enable telemetry
   */
  enable(): void {
    this.config.enabled = true;
  }

  /**
   * Disable telemetry
   */
  disable(): void {
    this.config.enabled = false;
  }

  /**
   * Check if telemetry is enabled
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Register a custom event handler
   */
  registerHandler(handler: TelemetryEventHandler): () => void {
    this.eventHandlers.push(handler);

    // Return unsubscribe function
    return () => {
      const index = this.eventHandlers.indexOf(handler);
      if (index > -1) {
        this.eventHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Track an authentication error
   */
  trackAuthError(
    error: AuthError,
    context?: {
      action?: string;
      userId?: string;
      duration?: number;
      retryCount?: number;
    }
  ): void {
    if (!this.config.enabled) {return;}

    // Apply sampling
    if (Math.random() > this.config.sampleRate) {return;}

    const event: AuthTelemetryEvent = {
      type: 'auth_error',
      timestamp: new Date(),
      errorCode: error.code,
      category: error.category,
      severity: error.severity,
      statusCode: error.statusCode,
      retryable: error.retryAfterSeconds !== undefined && error.retryAfterSeconds > 0,
      retryCount: context?.retryCount,
      duration: context?.duration,
      action: context?.action,
      userId: this.anonymizeId(context?.userId),
      sessionId: this.sessionId,
      userAgent: this.config.includeUserAgent && typeof navigator !== 'undefined'
        ? navigator.userAgent
        : undefined
    };

    this.processEvent(event);

    // Update error counts
    this.updateErrorCounts(error.code);
  }

  /**
   * Track a successful authentication
   */
  trackAuthSuccess(
    action: string,
    userId?: string,
    duration?: number
  ): void {
    if (!this.config.enabled) {return;}

    const event: AuthTelemetryEvent = {
      type: 'auth_success',
      timestamp: new Date(),
      action,
      duration,
      userId: this.anonymizeId(userId),
      sessionId: this.sessionId,
      userAgent: this.config.includeUserAgent && typeof navigator !== 'undefined'
        ? navigator.userAgent
        : undefined
    };

    this.processEvent(event);
  }

  /**
   * Track a retry attempt
   */
  trackRetry(
    error: AuthError,
    retryCount: number,
    action?: string
  ): void {
    if (!this.config.enabled) {return;}

    const event: AuthTelemetryEvent = {
      type: 'auth_retry',
      timestamp: new Date(),
      errorCode: error.code,
      category: error.category,
      severity: error.severity,
      retryCount,
      action,
      sessionId: this.sessionId
    };

    this.processEvent(event);
  }

  /**
   * Track error recovery
   */
  trackRecovery(
    originalError: AuthError,
    recoveryAction: string,
    success: boolean
  ): void {
    if (!this.config.enabled) {return;}

    const event: AuthTelemetryEvent = {
      type: 'auth_recovery',
      timestamp: new Date(),
      errorCode: originalError.code,
      category: originalError.category,
      action: recoveryAction,
      sessionId: this.sessionId,
      context: { success }
    };

    this.processEvent(event);
  }

  /**
   * Get current aggregated metrics
   */
  getMetrics(): AuthTelemetryMetrics {
    const now = new Date();
    const windowStart = new Date(now.getTime() - this.METRICS_WINDOW_MS);

    const recentEvents = this.metricsWindow.filter(
      e => e.timestamp >= windowStart
    );

    const errors = recentEvents.filter(e => e.type === 'auth_error');
    const successes = recentEvents.filter(e => e.type === 'auth_success');
    const total = errors.length + successes.length;

    // Group errors by code, category, severity
    const errorsByCode: Record<string, number> = {};
    const errorsByCategory: Record<string, number> = {};
    const errorsBySeverity: Record<string, number> = {};

    for (const error of errors) {
      if (error.errorCode) {
        errorsByCode[error.errorCode] = (errorsByCode[error.errorCode] || 0) + 1;
      }
      if (error.category) {
        errorsByCategory[error.category] = (errorsByCategory[error.category] || 0) + 1;
      }
      if (error.severity) {
        errorsBySeverity[error.severity] = (errorsBySeverity[error.severity] || 0) + 1;
      }
    }

    // Calculate averages
    const durations = recentEvents
      .filter(e => e.duration !== undefined)
      .map(e => e.duration!);

    const avgDuration = durations.length > 0
      ? durations.reduce((a, b) => a + b, 0) / durations.length
      : 0;

    const sortedDurations = [...durations].sort((a, b) => a - b);
    const p95Index = Math.floor(sortedDurations.length * 0.95);
    const p95Duration = sortedDurations[p95Index] || 0;

    const retryCounts = errors
      .filter(e => e.retryCount !== undefined)
      .map(e => e.retryCount!);

    const avgRetryCount = retryCounts.length > 0
      ? retryCounts.reduce((a, b) => a + b, 0) / retryCounts.length
      : 0;

    return {
      windowStart,
      windowEnd: now,
      totalErrors: errors.length,
      totalSuccesses: successes.length,
      errorRate: total > 0 ? errors.length / total : 0,
      errorsByCode,
      errorsByCategory,
      errorsBySeverity,
      avgRetryCount,
      avgDuration,
      p95Duration
    };
  }

  /**
   * Get error count for a specific error code
   */
  getErrorCount(code: AuthErrorCode): number {
    return this._errorCounts()[code] || 0;
  }

  /**
   * Reset error counts
   */
  resetErrorCounts(): void {
    this._errorCounts.set({} as Record<AuthErrorCode, number>);
  }

  /**
   * Clear all telemetry data
   */
  clearData(): void {
    this.eventBuffer = [];
    this.metricsWindow = [];
    this.resetErrorCounts();
    this.logger.debug('[AuthTelemetry] Data cleared');
  }

  /**
   * Flush buffered events to backend
   */
  flush(): Observable<void> {
    if (this.eventBuffer.length === 0) {
      return new Observable(subscriber => {
        subscriber.next();
        subscriber.complete();
      });
    }

    const events = [...this.eventBuffer];
    this.eventBuffer = [];

    if (!this.config.sendToBackend || !this.config.backendUrl) {
      return new Observable(subscriber => {
        subscriber.next();
        subscriber.complete();
      });
    }

    return this.http.post<void>(this.config.backendUrl, { events }).pipe(
      retry(2),
      catchError((err: HttpErrorResponse) => {
        this.logger.warn('[AuthTelemetry] Failed to send events to backend', { error: err });
        // Re-add events to buffer on failure (up to max size)
        this.eventBuffer = [...events, ...this.eventBuffer].slice(0, this.config.maxBufferSize);
        throw err;
      })
    );
  }

  /**
   * Process a telemetry event
   */
  private processEvent(event: AuthTelemetryEvent): void {
    // Add to metrics window
    this.metricsWindow.push(event);

    // Guard against unbounded growth
    if (this.metricsWindow.length > this.MAX_METRICS_WINDOW_SIZE) {
      // Remove oldest 10% of events
      const removeCount = Math.floor(this.MAX_METRICS_WINDOW_SIZE * 0.1);
      this.metricsWindow.splice(0, removeCount);
    }

    // Emit to stream
    this.eventsSubject.next(event);

    // Call custom handlers
    for (const handler of this.eventHandlers) {
      try {
        handler(event);
      } catch (err) {
        this.logger.error('[AuthTelemetry] Handler error', { error: String(err) });
      }
    }

    // Log to console if configured
    if (this.config.logToConsole) {
      this.logger.debug('[AuthTelemetry] Event', {
        type: event.type,
        errorCode: event.errorCode,
        category: event.category,
        severity: event.severity,
        timestamp: event.timestamp.toISOString()
      });
    }

    // Buffer for backend
    if (this.config.sendToBackend) {
      this.eventBuffer.push(event);

      // Flush if buffer is full
      if (this.eventBuffer.length >= this.config.batchSize) {
        this.flush().pipe(
          take(1),
          catchError(err => {
            this.logger.error('[AuthTelemetry] Flush error', { error: String(err) });
            return of(undefined);
          })
        ).subscribe(); // No error handler needed: catchError above handles flush errors
      }
    }
  }

  /**
   * Update error counts signal
   */
  private updateErrorCounts(code: AuthErrorCode): void {
    const currentCounts = this._errorCounts();
    const newCount = (currentCounts[code] || 0) + 1;
    this._errorCounts.set({ ...currentCounts, [code]: newCount });
  }

  /**
   * Clean up old events from metrics window
   */
  private cleanupMetricsWindow(): void {
    const cutoff = new Date(Date.now() - this.METRICS_WINDOW_MS);
    this.metricsWindow = this.metricsWindow.filter(e => e.timestamp >= cutoff);
  }

  /**
   * Generate a session ID
   */
  private generateSessionId(): string {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 10);
    return `${timestamp}-${random}`;
  }

  /**
   * Anonymize an ID for privacy using session-scoped pseudonymous mapping
   */
  private anonymizeId(id?: string): string | undefined {
    if (!id || !this.config.anonymizeIds) {return id;}

    // Use session-scoped pseudonymous cache with incrementing counter
    let pseudonymousId = this.pseudonymousCache.get(id);
    if (!pseudonymousId) {
      pseudonymousId = `user-${this.pseudonymousCounter++}`;
      this.pseudonymousCache.set(id, pseudonymousId);
    }
    return pseudonymousId;
  }
}
